/**
 * Face API Service
 * Handles face detection and landmark tracking using face-api.js
 */

import * as faceapi from 'face-api.js';

// Configuration
const MODEL_URL = '/models';
const DETECTION_OPTIONS = new faceapi.TinyFaceDetectorOptions({
  inputSize: 320,
  scoreThreshold: 0.5,
});

// Eye Aspect Ratio thresholds for blink detection
const EAR_THRESHOLD = 0.21; // Below this = eyes closed
const BLINK_FRAMES_THRESHOLD = 2; // Consecutive frames to confirm blink

class FaceAPIService {
  constructor() {
    this.modelsLoaded = false;
    this.isLoading = false;
  }

  /**
   * Load face-api.js models
   */
  async loadModels() {
    if (this.modelsLoaded) return true;
    if (this.isLoading) {
      // Wait for loading to complete
      while (this.isLoading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.modelsLoaded;
    }

    this.isLoading = true;
    
    try {
      
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
      ]);
      
      this.modelsLoaded = true;
      return true;
    } catch (error) {
      this.modelsLoaded = false;
      return false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Detect face and landmarks in a video element
   */
  async detectFace(videoElement) {
    if (!this.modelsLoaded) {
      await this.loadModels();
    }

    try {
      const detection = await faceapi
        .detectSingleFace(videoElement, DETECTION_OPTIONS)
        .withFaceLandmarks(true); // Use tiny model

      return detection || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Calculate Eye Aspect Ratio from landmarks
   * EAR = (|p2-p6| + |p3-p5|) / (2 * |p1-p4|)
   */
  calculateEAR(eyePoints) {
    if (!eyePoints || eyePoints.length < 6) return 1;

    const p1 = eyePoints[0]; // Left corner
    const p2 = eyePoints[1]; // Top-left
    const p3 = eyePoints[2]; // Top-right
    const p4 = eyePoints[3]; // Right corner
    const p5 = eyePoints[4]; // Bottom-right
    const p6 = eyePoints[5]; // Bottom-left

    // Vertical distances
    const v1 = Math.sqrt(Math.pow(p2.x - p6.x, 2) + Math.pow(p2.y - p6.y, 2));
    const v2 = Math.sqrt(Math.pow(p3.x - p5.x, 2) + Math.pow(p3.y - p5.y, 2));

    // Horizontal distance
    const h = Math.sqrt(Math.pow(p1.x - p4.x, 2) + Math.pow(p1.y - p4.y, 2));

    if (h === 0) return 1;
    
    return (v1 + v2) / (2.0 * h);
  }

  /**
   * Get eye points from face landmarks
   */
  getEyePoints(landmarks) {
    if (!landmarks) return { leftEye: null, rightEye: null };

    const positions = landmarks.positions;
    
    // Face-api.js 68-point landmarks:
    // Left eye: points 36-41
    // Right eye: points 42-47
    const leftEye = positions.slice(36, 42);
    const rightEye = positions.slice(42, 48);

    return { leftEye, rightEye };
  }

  /**
   * Calculate head rotation from landmarks
   * Returns angle in degrees: -90 (left) to 90 (right)
   */
  calculateHeadRotation(landmarks) {
    if (!landmarks) return 0;

    const positions = landmarks.positions;
    
    // Use nose tip (point 30) and face contour points
    const noseTip = positions[30];
    const leftCheek = positions[0];  // Leftmost face contour
    const rightCheek = positions[16]; // Rightmost face contour

    const faceWidth = rightCheek.x - leftCheek.x;
    const faceCenterX = (leftCheek.x + rightCheek.x) / 2;
    const noseOffsetX = noseTip.x - faceCenterX;

    // Normalize to -1 to 1, then scale to degrees
    const normalizedOffset = noseOffsetX / (faceWidth / 2);
    const rotationDegrees = normalizedOffset * 45; // Max 45 degrees detected

    return rotationDegrees;
  }

  /**
   * Get face center position for pause detection
   */
  getFaceCenter(detection) {
    if (!detection || !detection.detection) return null;

    const box = detection.detection.box;
    return {
      x: box.x + box.width / 2,
      y: box.y + box.height / 2,
      width: box.width,
      height: box.height,
    };
  }
}

// Singleton instance
export const faceAPIService = new FaceAPIService();

/**
 * Liveness Detector using face-api.js
 */
export class LivenessDetector {
  constructor() {
    this.reset();
  }

  reset() {
    this.blinkCount = 0;
    this.eyeClosedFrames = 0;
    this.wasEyeClosed = false;
    this.lastFaceCenter = null;
    this.pauseStartTime = null;
    this.pauseDetected = false;
    this.pauseDuration = 0;
    this.rotationHistory = [];
    this.earHistory = [];
  }

  /**
   * Process a detection result and update liveness state
   */
  processDetection(detection) {
    if (!detection) {
      return {
        faceDetected: false,
        blinked: false,
        isPaused: false,
        rotation: 0,
        ear: 1,
      };
    }

    const landmarks = detection.landmarks;
    const { leftEye, rightEye } = faceAPIService.getEyePoints(landmarks);
    
    // Calculate EAR
    const leftEAR = faceAPIService.calculateEAR(leftEye);
    const rightEAR = faceAPIService.calculateEAR(rightEye);
    const avgEAR = (leftEAR + rightEAR) / 2;

    // Store EAR history for debugging
    this.earHistory.push(avgEAR);
    if (this.earHistory.length > 30) this.earHistory.shift();

    // Detect blink
    const blinked = this.detectBlink(avgEAR);

    // Calculate rotation
    const rotation = faceAPIService.calculateHeadRotation(landmarks);
    this.rotationHistory.push({ rotation, timestamp: Date.now() });
    if (this.rotationHistory.length > 60) this.rotationHistory.shift();

    // Detect pause
    const faceCenter = faceAPIService.getFaceCenter(detection);
    const isPaused = this.detectPause(faceCenter);

    return {
      faceDetected: true,
      blinked,
      isPaused,
      rotation,
      ear: avgEAR,
      faceCenter,
    };
  }

  /**
   * Detect blink from EAR value
   */
  detectBlink(ear) {
    let blinkJustHappened = false;

    if (ear < EAR_THRESHOLD) {
      this.eyeClosedFrames++;
      if (this.eyeClosedFrames >= BLINK_FRAMES_THRESHOLD) {
        this.wasEyeClosed = true;
      }
    } else {
      if (this.wasEyeClosed && this.eyeClosedFrames >= BLINK_FRAMES_THRESHOLD) {
        // Eyes just opened after being closed = blink completed
        this.blinkCount++;
        blinkJustHappened = true;
      }
      this.eyeClosedFrames = 0;
      this.wasEyeClosed = false;
    }

    return blinkJustHappened;
  }

  /**
   * Detect if user is holding still (pause)
   */
  detectPause(faceCenter, requiredDurationMs = 2000) {
    if (!faceCenter) {
      this.pauseStartTime = null;
      return false;
    }

    const now = Date.now();
    const movementThreshold = 15; // pixels

    if (!this.lastFaceCenter) {
      this.lastFaceCenter = { ...faceCenter, timestamp: now };
      return false;
    }

    const dx = Math.abs(faceCenter.x - this.lastFaceCenter.x);
    const dy = Math.abs(faceCenter.y - this.lastFaceCenter.y);
    const movement = Math.sqrt(dx * dx + dy * dy);

    if (movement < movementThreshold) {
      // Face is relatively still
      if (!this.pauseStartTime) {
        this.pauseStartTime = now;
      }
      this.pauseDuration = now - this.pauseStartTime;
      
      if (this.pauseDuration >= requiredDurationMs && !this.pauseDetected) {
        this.pauseDetected = true;
        return true;
      }
    } else {
      // Face moved - reset
      this.pauseStartTime = now;
      this.pauseDuration = 0;
    }

    this.lastFaceCenter = { ...faceCenter, timestamp: now };
    return this.pauseDetected && this.pauseDuration >= requiredDurationMs;
  }

  /**
   * Check if rotation has covered required angles
   */
  getRotationCoverage() {
    const requiredAngles = [-30, 0, 30];
    const tolerance = 15;
    
    const covered = requiredAngles.map(angle => {
      return this.rotationHistory.some(h => Math.abs(h.rotation - angle) <= tolerance);
    });

    return {
      angles: requiredAngles,
      covered,
      complete: covered.every(c => c),
    };
  }

  getBlinkCount() {
    return this.blinkCount;
  }

  getPauseDuration() {
    return this.pauseDuration;
  }

  isPauseComplete() {
    return this.pauseDetected;
  }

  resetPause() {
    this.pauseDetected = false;
    this.pauseStartTime = null;
    this.pauseDuration = 0;
  }

  getAverageEAR() {
    if (this.earHistory.length === 0) return 0;
    return this.earHistory.reduce((a, b) => a + b, 0) / this.earHistory.length;
  }
}

export default faceAPIService;
export { EAR_THRESHOLD, BLINK_FRAMES_THRESHOLD };
